import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useAdminSocket from '../../hooks/useAdminSocket';

const DISMISS_KEY = 'admin.toast.dismissed';
const SOUND_KEY   = 'admin.toast.sound';

function loadDismissed() {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}
function saveDismissed(set) {
  try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...set])); } catch {}
}

// Tiny inline beep via Web Audio so we don't ship an mp3. Two pitches:
// high for HIGH-priority (payment failures), low for normal (new orders).
function playBeep(high) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = high ? 880 : 520;
    gain.gain.value = 0.06;
    osc.start();
    setTimeout(() => { osc.stop(); ctx.close(); }, 180);
  } catch {}
}

/**
 * Real-time admin notification tray. Subscribes to socket.io events emitted
 * by the server (orders, auctions, leads, payment failures, inventory) and
 * surfaces them as stacked toast cards in the bottom-right.
 *
 * Cards are clickable — tapping one routes to the relevant admin page so the
 * operator can act immediately. Dismissal is per-card; the connection
 * indicator at the top distinguishes "no events yet" from "socket dropped".
 */
const PRIORITIES = {
  'payments:failed':   { color: '#c0392b', label: 'Payment failed', high: true },
  'leads:new':         { color: '#3b4cca', label: 'New lead' },
  'orders:new':        { color: '#0d5c3a', label: 'New order' },
  'auction:ended':     { color: '#9b59b6', label: 'Auction ended' },
  'inventory:changed': { color: '#666',    label: 'Inventory' },
  'pricing:applied':   { color: '#666',    label: 'Pricing' },
};

export default function LiveEventsToast() {
  const isAdmin = typeof window !== 'undefined' && !!localStorage.getItem('admin_token');
  return isAdmin ? <Inner /> : null;
}

function Inner() {
  const { events, connected } = useAdminSocket();
  // sessionStorage persists dismissals across nav within the admin session
  // so a payment-failed alert you've already acknowledged doesn't reappear
  // when you click into another admin page.
  const [dismissed, setDismissed] = useState(() => loadDismissed());
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem(SOUND_KEY) !== '0');
  const lastSeenAt = useRef(0);

  // Persist dismissals + play optional beep on truly new high-priority events.
  useEffect(() => { saveDismissed(dismissed); }, [dismissed]);
  useEffect(() => {
    if (!soundOn || !events.length) return;
    const newest = events[0];
    if (!newest || newest.at <= lastSeenAt.current) return;
    lastSeenAt.current = newest.at;
    if (PRIORITIES[newest.kind]?.high) playBeep(true);
    else if (newest.kind === 'orders:new') playBeep(false);
  }, [events, soundOn]);

  const visible = events.filter(e => !dismissed.has(`${e.at}-${e.kind}`)).slice(0, 6);

  if (!visible.length && connected) {
    return (
      <div style={{
        position: 'fixed', bottom: 12, right: 12,
        fontSize: 10, color: '#0a0', opacity: 0.6,
        zIndex: 9998, pointerEvents: 'none',
      }}>● live</div>
    );
  }

  return (
    <div style={{
      position: 'fixed', bottom: 16, right: 16, width: 340, maxWidth: 'calc(100vw - 24px)',
      zIndex: 9999,
    }}>
      <div style={{ fontSize: 11, color: connected ? '#0a0' : '#888', textAlign: 'right', marginBottom: 4, display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
        <button
          onClick={() => { const next = !soundOn; setSoundOn(next); localStorage.setItem(SOUND_KEY, next ? '1' : '0'); }}
          title={soundOn ? 'Mute notifications' : 'Unmute notifications'}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#888', fontSize: 14, padding: 0 }}
        >{soundOn ? '🔔' : '🔕'}</button>
        <span>● {connected ? 'live' : 'reconnecting…'}</span>
      </div>
      {visible.map((e, i) => {
        const meta = PRIORITIES[e.kind] || { color: '#444', label: e.kind };
        const link = linkFor(e);
        const key = `${e.at}-${e.kind}`;
        const Wrapper = link ? Link : 'div';
        const wrapperProps = link ? { to: link } : {};
        return (
          <Wrapper
            key={key}
            {...wrapperProps}
            style={{
              display: 'block',
              background: '#1f2937', color: '#fff', textDecoration: 'none',
              borderLeft: `4px solid ${meta.color}`,
              padding: '10px 12px',
              borderRadius: 8,
              marginBottom: 6, fontSize: 12,
              boxShadow: meta.high ? `0 4px 12px ${meta.color}66` : '0 2px 6px rgba(0,0,0,0.3)',
              opacity: 1 - i * 0.08,
              cursor: link ? 'pointer' : 'default',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ color: meta.color }}>{meta.label}</strong>
                <div style={{ marginTop: 2 }}>{describe(e)}</div>
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                  {new Date(e.at).toLocaleTimeString()}
                </div>
              </div>
              <button
                onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); setDismissed(s => new Set([...s, key])); }}
                aria-label="Dismiss"
                style={{
                  background: 'transparent', border: 'none', color: '#9ca3af',
                  fontSize: 16, lineHeight: 1, cursor: 'pointer', padding: '0 4px',
                }}
              >×</button>
            </div>
          </Wrapper>
        );
      })}
    </div>
  );
}

function describe(e) {
  const p = e.payload || {};
  switch (e.kind) {
    case 'orders:new':        return `#${p.orderId} — $${Number(p.total || 0).toFixed(2)} from ${p.customer || 'guest'}`;
    case 'payments:failed':   return `Order #${p.orderId} — ${p.reason || 'reason unknown'}`;
    case 'leads:new':         return `${p.name || p.email || 'Anonymous'} — ${p.question || 'no message'}`;
    case 'auction:ended':     return `Auction #${p.auctionId} → order #${p.orderId}`;
    case 'inventory:changed': return `Product #${p.productId} ${p.delta > 0 ? '+' : ''}${p.delta} → ${p.newBalance}`;
    case 'pricing:applied':   return `Product #${p.productId} $${p.oldPrice} → $${p.newPrice}`;
    default: return JSON.stringify(p).slice(0, 80);
  }
}

function linkFor(e) {
  const p = e.payload || {};
  switch (e.kind) {
    case 'orders:new':
    case 'payments:failed':   return p.orderId ? `/admin/orders/${p.orderId}` : '/admin/orders';
    case 'leads:new':         return '/admin/chatbot';
    case 'auction:ended':     return '/admin/orders';
    case 'inventory:changed':
    case 'pricing:applied':   return p.productId ? `/admin/products/${p.productId}` : '/admin/products';
    default: return null;
  }
}
